export const THEME_STORAGE_KEY = 'portal-theme-mode';
export const BRAND_STORAGE_KEY = 'portal-brand-color';
export const TIME_FORMAT_STORAGE_KEY = 'portal-time-format';
export const SIDEBAR_STYLE_STORAGE_KEY = 'portal-sidebar-style';
export const PRIMARY_BUTTON_COLOR_STORAGE_KEY = 'portal-primary-button-color';
export const SECONDARY_BUTTON_COLOR_STORAGE_KEY = 'portal-secondary-button-color';
const DASHBOARD_PREFS_STORAGE_KEY = 'dashboard-preferences-v1';
export const THEME = {
  LIGHT: 'light',
  DARK: 'dark',
};
export const BRAND = {
  INDIGO: 'indigo',
  BLUE: 'blue',
  GREEN: 'green',
  PURPLE: 'purple',
  ORANGE: 'orange',
};
export const TIME_FORMAT = {
  TWELVE_HOUR: '12h',
  TWENTY_FOUR_HOUR: '24h',
};
export const SIDEBAR_STYLE = {
  LIGHT: 'light',
  DARK: 'dark',
  BRAND: 'brand',
};

const BUTTON_COLORS = {
  indigo: '#2537c6',
  blue: '#2f68ea',
  green: '#089c69',
  purple: '#7b3fe4',
  orange: '#ee5f00',
};

const BRAND_COLORS = {
  [BRAND.INDIGO]: { primary: '#2537c6', strong: '#1e2fb1', soft: '#e8ecff' },
  [BRAND.BLUE]: { primary: '#2f68ea', strong: '#1f54d0', soft: '#e7f0ff' },
  [BRAND.GREEN]: { primary: '#089c69', strong: '#067a53', soft: '#e5f7ef' },
  [BRAND.PURPLE]: { primary: '#7b3fe4', strong: '#5d2fb3', soft: '#f0e8ff' },
  [BRAND.ORANGE]: { primary: '#ee5f00', strong: '#cc4f00', soft: '#fff0e5' },
};

export function readThemePreference() {
  try {
    const directTheme = localStorage.getItem(THEME_STORAGE_KEY);
    if (directTheme === THEME.DARK || directTheme === THEME.LIGHT) {
      return directTheme;
    }

    const dashboardPrefsRaw = localStorage.getItem(DASHBOARD_PREFS_STORAGE_KEY);
    if (dashboardPrefsRaw) {
      const dashboardPrefs = JSON.parse(dashboardPrefsRaw);
      if (dashboardPrefs?.themeMode === THEME.DARK || dashboardPrefs?.themeMode === THEME.LIGHT) {
        return dashboardPrefs.themeMode;
      }
    }
  } catch {
    // fall through to default
  }

  return THEME.LIGHT;
}

export function readBrandColorPreference() {
  try {
    const directBrand = localStorage.getItem(BRAND_STORAGE_KEY);
    if (directBrand && BRAND_COLORS[directBrand]) return directBrand;

    const dashboardPrefsRaw = localStorage.getItem(DASHBOARD_PREFS_STORAGE_KEY);
    if (dashboardPrefsRaw) {
      const dashboardPrefs = JSON.parse(dashboardPrefsRaw);
      const brandFromPrefs = dashboardPrefs?.brandColor;
      if (brandFromPrefs && BRAND_COLORS[brandFromPrefs]) return brandFromPrefs;
    }
  } catch {
    // fall through to default
  }

  return BRAND.INDIGO;
}

export function readTimeFormatPreference() {
  try {
    const directTimeFormat = localStorage.getItem(TIME_FORMAT_STORAGE_KEY);
    if (
      directTimeFormat === TIME_FORMAT.TWELVE_HOUR ||
      directTimeFormat === TIME_FORMAT.TWENTY_FOUR_HOUR
    ) {
      return directTimeFormat;
    }

    const dashboardPrefsRaw = localStorage.getItem(DASHBOARD_PREFS_STORAGE_KEY);
    if (dashboardPrefsRaw) {
      const dashboardPrefs = JSON.parse(dashboardPrefsRaw);
      if (
        dashboardPrefs?.timeFormat === TIME_FORMAT.TWELVE_HOUR ||
        dashboardPrefs?.timeFormat === TIME_FORMAT.TWENTY_FOUR_HOUR
      ) {
        return dashboardPrefs.timeFormat;
      }
    }
  } catch {
    // fall through to default
  }

  return TIME_FORMAT.TWELVE_HOUR;
}

export function readSidebarStylePreference() {
  try {
    const directSidebarStyle = localStorage.getItem(SIDEBAR_STYLE_STORAGE_KEY);
    if (
      directSidebarStyle === SIDEBAR_STYLE.LIGHT ||
      directSidebarStyle === SIDEBAR_STYLE.DARK ||
      directSidebarStyle === SIDEBAR_STYLE.BRAND
    ) {
      return directSidebarStyle;
    }

    const dashboardPrefsRaw = localStorage.getItem(DASHBOARD_PREFS_STORAGE_KEY);
    if (dashboardPrefsRaw) {
      const dashboardPrefs = JSON.parse(dashboardPrefsRaw);
      if (
        dashboardPrefs?.sidebarStyle === SIDEBAR_STYLE.LIGHT ||
        dashboardPrefs?.sidebarStyle === SIDEBAR_STYLE.DARK ||
        dashboardPrefs?.sidebarStyle === SIDEBAR_STYLE.BRAND
      ) {
        return dashboardPrefs.sidebarStyle;
      }
    }
  } catch {
    // fall through to default
  }

  return SIDEBAR_STYLE.BRAND;
}

function isSupportedButtonColor(value) {
  return !!BUTTON_COLORS[String(value || '').trim()];
}

export function readPrimaryButtonColorPreference() {
  try {
    const directColor = localStorage.getItem(PRIMARY_BUTTON_COLOR_STORAGE_KEY);
    if (isSupportedButtonColor(directColor)) return directColor;

    const dashboardPrefsRaw = localStorage.getItem(DASHBOARD_PREFS_STORAGE_KEY);
    if (dashboardPrefsRaw) {
      const dashboardPrefs = JSON.parse(dashboardPrefsRaw);
      if (isSupportedButtonColor(dashboardPrefs?.primaryButtonColor)) {
        return dashboardPrefs.primaryButtonColor;
      }
    }
  } catch {
    // fall through to default
  }
  return 'indigo';
}

export function readSecondaryButtonColorPreference() {
  try {
    const directColor = localStorage.getItem(SECONDARY_BUTTON_COLOR_STORAGE_KEY);
    if (isSupportedButtonColor(directColor)) return directColor;

    const dashboardPrefsRaw = localStorage.getItem(DASHBOARD_PREFS_STORAGE_KEY);
    if (dashboardPrefsRaw) {
      const dashboardPrefs = JSON.parse(dashboardPrefsRaw);
      if (isSupportedButtonColor(dashboardPrefs?.secondaryButtonColor)) {
        return dashboardPrefs.secondaryButtonColor;
      }
    }
  } catch {
    // fall through to default
  }
  return 'blue';
}

export function formatTimeByPreference(value, timeFormat, withSeconds = false) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const resolved = timeFormat === TIME_FORMAT.TWENTY_FOUR_HOUR
    ? TIME_FORMAT.TWENTY_FOUR_HOUR
    : TIME_FORMAT.TWELVE_HOUR;

  return date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    ...(withSeconds ? { second: '2-digit' } : {}),
    hour12: resolved !== TIME_FORMAT.TWENTY_FOUR_HOUR,
  });
}

export function applyBrandColorToDocument(brandKey) {
  if (typeof document === 'undefined') return;
  const palette = BRAND_COLORS[brandKey] || BRAND_COLORS[BRAND.INDIGO];
  document.documentElement.style.setProperty('--portal-primary', palette.primary);
  document.documentElement.style.setProperty('--portal-primary-strong', palette.strong);
  document.documentElement.style.setProperty('--portal-primary-soft', palette.soft);
}

export function applyThemeToDocument(theme) {
  if (typeof document === 'undefined') return;
  const normalizedTheme = theme === THEME.DARK ? THEME.DARK : THEME.LIGHT;
  document.body.classList.toggle('dark-mode', normalizedTheme === THEME.DARK);
  document.documentElement.style.colorScheme = normalizedTheme;
}

export function applySidebarStyleToDocument(sidebarStyle) {
  if (typeof document === 'undefined') return;
  const normalized =
    sidebarStyle === SIDEBAR_STYLE.LIGHT ||
    sidebarStyle === SIDEBAR_STYLE.DARK ||
    sidebarStyle === SIDEBAR_STYLE.BRAND
      ? sidebarStyle
      : SIDEBAR_STYLE.BRAND;

  document.body.classList.remove(
    'sidebar-style-light',
    'sidebar-style-dark',
    'sidebar-style-brand',
  );
  document.body.classList.add(`sidebar-style-${normalized}`);
}

export function applyButtonColorsToDocument(primaryColorKey, secondaryColorKey) {
  if (typeof document === 'undefined') return;
  const primary = BUTTON_COLORS[primaryColorKey] || BUTTON_COLORS.indigo;
  const secondary = BUTTON_COLORS[secondaryColorKey] || BUTTON_COLORS.blue;
  document.documentElement.style.setProperty('--portal-button-primary', primary);
  document.documentElement.style.setProperty('--portal-button-secondary', secondary);
}

export function setThemePreference(theme) {
  const normalizedTheme = theme === THEME.DARK ? THEME.DARK : THEME.LIGHT;
  localStorage.setItem(THEME_STORAGE_KEY, normalizedTheme);
  applyThemeToDocument(normalizedTheme);
  window.dispatchEvent(new CustomEvent('portal-theme-change', { detail: { theme: normalizedTheme } }));
}

export function setBrandColorPreference(brandKey) {
  const normalizedBrand = BRAND_COLORS[brandKey] ? brandKey : BRAND.INDIGO;
  localStorage.setItem(BRAND_STORAGE_KEY, normalizedBrand);
  applyBrandColorToDocument(normalizedBrand);
  window.dispatchEvent(
    new CustomEvent('portal-theme-change', { detail: { brandColor: normalizedBrand } }),
  );
}

export function setSidebarStylePreference(sidebarStyle) {
  const normalized =
    sidebarStyle === SIDEBAR_STYLE.LIGHT ||
    sidebarStyle === SIDEBAR_STYLE.DARK ||
    sidebarStyle === SIDEBAR_STYLE.BRAND
      ? sidebarStyle
      : SIDEBAR_STYLE.BRAND;
  localStorage.setItem(SIDEBAR_STYLE_STORAGE_KEY, normalized);
  applySidebarStyleToDocument(normalized);
  window.dispatchEvent(
    new CustomEvent('portal-theme-change', { detail: { sidebarStyle: normalized } }),
  );
}

export function setPrimaryButtonColorPreference(colorKey) {
  const normalized = isSupportedButtonColor(colorKey) ? colorKey : 'indigo';
  localStorage.setItem(PRIMARY_BUTTON_COLOR_STORAGE_KEY, normalized);
  applyButtonColorsToDocument(
    normalized,
    readSecondaryButtonColorPreference(),
  );
  window.dispatchEvent(
    new CustomEvent('portal-theme-change', { detail: { primaryButtonColor: normalized } }),
  );
}

export function setSecondaryButtonColorPreference(colorKey) {
  const normalized = isSupportedButtonColor(colorKey) ? colorKey : 'blue';
  localStorage.setItem(SECONDARY_BUTTON_COLOR_STORAGE_KEY, normalized);
  applyButtonColorsToDocument(
    readPrimaryButtonColorPreference(),
    normalized,
  );
  window.dispatchEvent(
    new CustomEvent('portal-theme-change', { detail: { secondaryButtonColor: normalized } }),
  );
}

export function setTimeFormatPreference(timeFormat) {
  const normalized =
    timeFormat === TIME_FORMAT.TWENTY_FOUR_HOUR
      ? TIME_FORMAT.TWENTY_FOUR_HOUR
      : TIME_FORMAT.TWELVE_HOUR;
  localStorage.setItem(TIME_FORMAT_STORAGE_KEY, normalized);
  window.dispatchEvent(new CustomEvent('portal-time-format-change', { detail: { timeFormat: normalized } }));
}

/** Apply saved theme (localStorage + dashboard-preferences-v1) before first paint of admin chrome. */
export function applyPortalThemeFromStorage() {
  if (typeof document === 'undefined') return;
  applyThemeToDocument(readThemePreference());
  applyBrandColorToDocument(readBrandColorPreference());
  applySidebarStyleToDocument(readSidebarStylePreference());
  applyButtonColorsToDocument(
    readPrimaryButtonColorPreference(),
    readSecondaryButtonColorPreference(),
  );
}
